import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EmailService } from '../../../core/services/email.service';
import { AuthService } from '../../../features/auth/services/auth.service';
import { NotificationService } from '@core/services/notification.service';

interface ContactTopic {
  id: string;
  icon: string;
  email: string;
  subjectKey: string;
}

@Component({
  selector: 'app-contact-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatRippleModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    TranslateModule
  ],
  templateUrl: './contact-dialog.component.html',
  styleUrl: './contact-dialog.component.scss'
})
export class ContactDialogComponent implements OnInit {
  private authService = inject(AuthService);

  selectedTopic: ContactTopic | null = null;
  showForm = false;
  isLoading = false;
  contactForm: FormGroup;
  isRtl = false;

  topics: ContactTopic[] = [
    {
      id: 'technical',
      icon: 'build',
      email: 'support@ezat.work',
      subjectKey: 'contact.topics.technical.defaultSubject'
    },
    {
      id: 'billing',
      icon: 'payment',
      email: 'billing@ezat.work',
      subjectKey: 'contact.topics.billing.defaultSubject'
    },
    {
      id: 'partnerships',
      icon: 'handshake',
      email: 'partnerships@ezat.work',
      subjectKey: 'contact.topics.partnerships.defaultSubject'
    },
    {
      id: 'feedback',
      icon: 'lightbulb',
      email: 'feedback@ezat.work',
      subjectKey: 'contact.topics.feedback.defaultSubject'
    },
    {
      id: 'security',
      icon: 'security',
      email: 'security@ezat.work',
      subjectKey: 'contact.topics.security.defaultSubject'
    },
    {
      id: 'general',
      icon: 'mail',
      email: 'info@ezat.work',
      subjectKey: 'contact.topics.general.defaultSubject'
    }
  ];

  constructor(
    private dialogRef: MatDialogRef<ContactDialogComponent>,
    private fb: FormBuilder,
    private emailService: EmailService,
    private snackBar: MatSnackBar,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {
    this.contactForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email]],
      message: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(5000)]]
    });

    // Check RTL
    this.isRtl = document.dir === 'rtl' || document.documentElement.dir === 'rtl';
  }

  ngOnInit(): void {
    // Pre-fill user data from AuthService
    const user = this.authService.user();
    if (user) {
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
      this.contactForm.patchValue({
        name: fullName || '',
        email: user.email || ''
      });
    }
  }

  selectTopic(topic: ContactTopic): void {
    this.selectedTopic = topic;
  }

  showContactForm(): void {
    if (!this.selectedTopic) return;
    this.showForm = true;
  }

  backToTopics(): void {
    this.showForm = false;
  }

  sendMessage(): void {
    if (!this.selectedTopic || this.contactForm.invalid) return;

    this.isLoading = true;
    const formValue = this.contactForm.value;

    this.emailService.sendContactEmail({
      fromEmail: formValue.email,
      fromName: formValue.name,
      topic: this.selectedTopic.id,
      message: formValue.message
    }).subscribe({
      next: () => {
        this.isLoading = false;
        this.notificationService.success(this.translate.instant('contact.success') || 'Message sent successfully!');
        this.dialogRef.close(true);
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error sending contact email:', error);
        this.notificationService.error(this.translate.instant('contact.error') || 'Failed to send message. Please try again.');
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
